/**
 * VehicleServiceTab - Service-flik i fordonskortet
 * Visar servicepunkter och möjlighet att hantera service.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  WrenchScrewdriverIcon, CheckCircleIcon,
  ExclamationTriangleIcon, CalendarDaysIcon,
  ChevronDownIcon, ChevronUpIcon, PlusIcon,
} from '@heroicons/react/24/outline';
import { useTranslation } from '@/store/languageStore';
import { serviceApi, ServicePoint, ServiceHistory } from '@/services/service/serviceApi';
import { isInspectionServicePoint } from '@/utils/serviceDueHorizon';
import AddServicePointModal from '@/components/service/AddServicePointModal';

import DateInput from '@/components/common/DateInput';

interface VehicleServiceTabProps {
  vehicleId: number;
}

const statusConfig: Record<string, { bg: string; text: string; icon: React.ElementType }> = {
  OK: { bg: 'bg-green-100 text-green-800', text: 'OK', icon: CheckCircleIcon },
  WARNING: { bg: 'bg-yellow-100 text-yellow-800', text: 'Förfaller snart', icon: ExclamationTriangleIcon },
  OVERDUE: { bg: 'bg-red-100 text-red-800', text: 'Förfallen', icon: ExclamationTriangleIcon },
  BOOKED: { bg: 'bg-blue-100 text-blue-800', text: 'Bokad', icon: CalendarDaysIcon },
  COMPLETED: { bg: 'bg-gray-100 text-textSecondary', text: 'Utförd', icon: CheckCircleIcon },
};

const VehicleServiceTab: React.FC<VehicleServiceTabProps> = ({ vehicleId }) => {
  const { t } = useTranslation();
  const [servicePoints, setServicePoints] = useState<ServicePoint[]>([]);
  const [history, setHistory] = useState<ServiceHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Booking form state
  const [bookingPointId, setBookingPointId] = useState<string | null>(null);
  const [bookingDate, setBookingDate] = useState('');
  const [bookingNotes, setBookingNotes] = useState('');

  const [showAddPointModal, setShowAddPointModal] = useState(false);

  // Complete form state
  const [completePointId, setCompletePointId] = useState<string | null>(null);
  const [completeDate, setCompleteDate] = useState(new Date().toISOString().split('T')[0]);
  const [completeKm, setCompleteKm] = useState('');
  const [completeCost, setCompleteCost] = useState('');
  const [completeNotes, setCompleteNotes] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [sp, h] = await Promise.all([
        serviceApi.getServicePoints(vehicleId),
        serviceApi.getServiceHistory(vehicleId),
      ]);
      setServicePoints(sp);
      setHistory(h);
    } catch (err) {
      console.error('Failed to load service data:', err);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleSyncCarInfo = async () => {
    try {
      const count = await serviceApi.syncCarInfo(vehicleId);
      if (count > 0) await loadData();
    } catch (err) {
      console.error('Sync failed:', err);
    }
  };

  const handleBook = async (pointId: string) => {
    try {
      await serviceApi.updateServicePoint(pointId, {
        status: 'BOOKED',
        bookingDate: bookingDate || null,
        bookingNotes: bookingNotes || null,
      });
      setBookingPointId(null);
      setBookingDate('');
      setBookingNotes('');
      await loadData();
    } catch (err) {
      console.error('Booking failed:', err);
    }
  };

  const handleComplete = async (pointId: string) => {
    try {
      await serviceApi.completeServicePoint(pointId, {
        performedDate: completeDate,
        performedKm: completeKm ? parseInt(completeKm) : undefined,
        costSek: completeCost ? parseFloat(completeCost) : undefined,
        notes: completeNotes || undefined,
      });
      setCompletePointId(null);
      setCompleteDate(new Date().toISOString().split('T')[0]);
      setCompleteKm('');
      setCompleteCost('');
      setCompleteNotes('');
      await loadData();
    } catch (err) {
      console.error('Complete failed:', err);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  const pointHistory = (pointId: string) => history.filter(h => h.servicePointId === pointId);
  // Visa ALLA servicepunkter på fordonet oavsett hur långt fram nästa tillfälle ligger.
  const visibleServicePoints = servicePoints;

  return (
    <div className="space-y-6">
      {/* Service Points */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <WrenchScrewdriverIcon className="h-4 w-4" />
              {t('service.servicePoints') || 'Servicepunkter'}
            </h3>
            <p className="text-xs text-textTertiary mt-0.5">{t('service.allPointsHint')}</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddPointModal(true)}
              className="text-xs px-3 py-1 border rounded hover:bg-gray-50 text-textSecondary flex items-center gap-1"
            >
              <PlusIcon className="h-3.5 w-3.5" />
              {t('service.addPoint') || 'Lägg till'}
            </button>
            <button
              onClick={handleSyncCarInfo}
              className="text-xs px-3 py-1 border rounded hover:bg-gray-50 text-textSecondary"
            >
              {t('service.syncCarInfo') || 'Synka från Car.info'}
            </button>
          </div>
        </div>

        {servicePoints.length === 0 ? (
          <div className="text-center py-6 text-textTertiary text-sm border rounded-lg">
            {t('service.noServicePoints') || 'Inga servicepunkter. Klicka "Synka från Car.info" för att importera.'}
          </div>
        ) : (
          <>
            {/* Kolumnrubriker */}
            <div className="grid grid-cols-[1fr_100px_120px_120px_80px_24px] gap-2 px-4 py-2 text-xs font-medium text-textTertiary uppercase border-b">
              <div>Servicetyp</div>
              <div className="text-right">Intervall</div>
              <div className="text-right">{t('serviceOverview.nextKm')}</div>
              <div className="text-right">Ber. datum</div>
              <div className="text-center">Status</div>
              <div></div>
            </div>
            <div className="border rounded-lg divide-y">
            {visibleServicePoints.map(sp => {
              const cfg = statusConfig[sp.status] || statusConfig.OK;
              const StatusIcon = cfg.icon;
              const isExpanded = expandedId === sp.id;
              const spHistory = pointHistory(sp.id);

              return (
                <div key={sp.id}>
                  <button
                    onClick={() => setExpandedId(isExpanded ? null : sp.id)}
                    className="w-full px-4 py-3 grid grid-cols-[1fr_100px_120px_120px_80px_24px] gap-2 items-center hover:bg-gray-50 text-left"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <StatusIcon className={`h-4 w-4 flex-shrink-0 ${sp.status === 'OVERDUE' ? 'text-red-500' : sp.status === 'WARNING' ? 'text-yellow-500' : sp.status === 'BOOKED' ? 'text-blue-500' : 'text-green-500'}`} />
                      <span className="font-medium text-sm truncate">{sp.serviceTypeName || sp.serviceTypeCode}</span>
                    </div>
                    <div className="text-xs text-textTertiary text-right">
                      {isInspectionServicePoint(sp)
                        ? (t('service.inspectionDateOnly') || 'Datum')
                        : (
                          <>
                            {sp.intervalKm ? `${sp.intervalKm.toLocaleString()} km` : ''}
                            {sp.intervalKm && sp.intervalMonths ? ' / ' : ''}
                            {sp.intervalMonths ? (sp.intervalMonths >= 12 ? `${sp.intervalMonths / 12} år` : `${sp.intervalMonths} mån`) : ''}
                          </>
                        )}
                    </div>
                    <div className="text-sm text-right font-medium">
                      {isInspectionServicePoint(sp)
                        ? (sp.nextDueDate || '-')
                        : (sp.nextDueKm != null ? `${sp.nextDueKm.toLocaleString()} km` : '-')}
                    </div>
                    <div className="text-sm text-textTertiary text-right">
                      {isInspectionServicePoint(sp)
                        ? (sp.nextDueDate || '-')
                        : (sp.estimatedDueDate ? `~${sp.estimatedDueDate}` : sp.nextDueDate || '-')}
                    </div>
                    <div className="flex justify-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.bg}`}>
                        {t(`service.status.${sp.status.toLowerCase()}`) || cfg.text}
                      </span>
                    </div>
                    {isExpanded ? <ChevronUpIcon className="h-4 w-4 text-textTertiary" /> : <ChevronDownIcon className="h-4 w-4 text-textTertiary" />}
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 bg-gray-50 border-t">
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 py-3 text-sm">
                        <div>
                          <span className="text-textTertiary text-xs">{t('service.lastPerformed') || 'Senast utförd'}:</span>
                          <div>
                            {sp.lastPerformedDate || '-'}
                            {!isInspectionServicePoint(sp) && sp.lastPerformedKm ? ` (${sp.lastPerformedKm.toLocaleString()} km)` : ''}
                          </div>
                        </div>
                        <div>
                          <span className="text-textTertiary text-xs">{t('service.nextDue') || 'Nästa service'}:</span>
                          <div>
                            {isInspectionServicePoint(sp) ? (
                              <span>{sp.nextDueDate || '-'}</span>
                            ) : (
                              <>
                                {sp.nextDueKm != null && <span>{sp.nextDueKm.toLocaleString()} km</span>}
                                {sp.estimatedDueDate && <span className="text-textTertiary ml-1">(~{sp.estimatedDueDate})</span>}
                              </>
                            )}
                          </div>
                          {!isInspectionServicePoint(sp) && sp.currentKm != null && (
                            <div className="text-xs text-textTertiary">
                              Nu: {sp.currentKm.toLocaleString()} km
                              {sp.nextDueKm != null && sp.currentKm != null && sp.nextDueKm > sp.currentKm && (
                                <span> ({(sp.nextDueKm - sp.currentKm).toLocaleString()} km kvar)</span>
                              )}
                            </div>
                          )}
                        </div>
                        <div>
                          <span className="text-textTertiary text-xs">{t('service.reminder') || 'Påminnelse'}:</span>
                          <div>
                            {isInspectionServicePoint(sp)
                              ? (sp.warningDays ? `${sp.warningDays} ${t('service.days') || 'dagar'}` : '')
                              : (
                                <>
                                  {sp.warningKm ? `${sp.warningKm.toLocaleString()} km` : ''}
                                  {sp.warningDays ? ` ${sp.warningDays} ${t('service.days') || 'dagar'}` : ''}
                                </>
                              )}
                          </div>
                          {!isInspectionServicePoint(sp) && sp.avgKmPerDay != null && sp.avgKmPerDay > 0 && (
                            <div className="text-xs text-textTertiary">Snitt: {sp.avgKmPerDay} km/dag</div>
                          )}
                        </div>
                        {sp.bookingDate && (
                          <div>
                            <span className="text-textTertiary text-xs">{t('service.booking') || 'Bokning'}:</span>
                            <div>{sp.bookingDate} {sp.workshopName ? `@ ${sp.workshopName}` : ''}</div>
                            {sp.bookingNotes && <div className="text-xs text-textTertiary">{sp.bookingNotes}</div>}
                          </div>
                        )}
                      </div>

                      {/* History */}
                      {spHistory.length > 0 && (
                        <div className="mt-2">
                          <span className="text-textTertiary text-xs font-medium">{t('service.history') || 'Historik'}:</span>
                          <div className="mt-1 space-y-1">
                            {spHistory.slice(0, 5).map(h => (
                              <div key={h.id} className="flex items-center gap-3 text-xs text-textSecondary">
                                <span>{h.performedDate}</span>
                                {h.performedKm && <span>{h.performedKm.toLocaleString()} km</span>}
                                {h.costSek && <span>{h.costSek.toLocaleString()} kr</span>}
                                {h.workshopName && <span className="text-textTertiary">{h.workshopName}</span>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Actions */}
                      <div className="flex gap-2 mt-3">
                        {sp.status !== 'BOOKED' && sp.status !== 'COMPLETED' && (
                          <button
                            onClick={() => setBookingPointId(bookingPointId === sp.id ? null : sp.id)}
                            className="px-3 py-1.5 text-xs border rounded hover:bg-blue-50 text-blue-600 border-blue-200"
                          >
                            <CalendarDaysIcon className="h-3.5 w-3.5 inline mr-1" />
                            {t('service.book') || 'Boka'}
                          </button>
                        )}
                        <button
                          onClick={() => setCompletePointId(completePointId === sp.id ? null : sp.id)}
                          className="px-3 py-1.5 text-xs border rounded hover:bg-green-50 text-green-600 border-green-200"
                        >
                          <CheckCircleIcon className="h-3.5 w-3.5 inline mr-1" />
                          {t('service.markComplete') || 'Markera klar'}
                        </button>
                      </div>

                      {/* Booking form */}
                      {bookingPointId === sp.id && (
                        <div className="mt-3 p-3 border rounded bg-white space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-textTertiary">{t('service.bookingDate') || 'Bokningsdatum'}</label>
                              <DateInput value={bookingDate} onChange={e => setBookingDate(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                            </div>
                            <div>
                              <label className="text-xs text-textTertiary">{t('service.notes') || 'Anteckningar'}</label>
                              <input type="text" value={bookingNotes} onChange={e => setBookingNotes(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                            </div>
                          </div>
                          <button onClick={() => handleBook(sp.id)} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">
                            {t('service.confirmBooking') || 'Bekräfta bokning'}
                          </button>
                        </div>
                      )}

                      {/* Complete form */}
                      {completePointId === sp.id && (
                        <div className="mt-3 p-3 border rounded bg-white space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-xs text-textTertiary">{t('service.performedDate') || 'Utfört datum'}</label>
                              <DateInput value={completeDate} onChange={e => setCompleteDate(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                            </div>
                            <div>
                              <label className="text-xs text-textTertiary">{t('service.performedKm') || 'Mätarställning (km)'}</label>
                              <input type="number" value={completeKm} onChange={e => setCompleteKm(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                            </div>
                            <div>
                              <label className="text-xs text-textTertiary">{t('service.cost') || 'Kostnad (kr)'}</label>
                              <input type="number" value={completeCost} onChange={e => setCompleteCost(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                            </div>
                            <div>
                              <label className="text-xs text-textTertiary">{t('service.notes') || 'Anteckningar'}</label>
                              <input type="text" value={completeNotes} onChange={e => setCompleteNotes(e.target.value)} className="w-full px-2 py-1.5 border rounded text-sm" />
                            </div>
                          </div>
                          <button onClick={() => handleComplete(sp.id)} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded hover:bg-green-700">
                            {t('service.confirmComplete') || 'Markera som utförd'}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          </>
        )}
      </div>

      {/* Modals */}
      {showAddPointModal && (
        <AddServicePointModal
          vehicleId={vehicleId}
          onClose={() => setShowAddPointModal(false)}
          onCreated={loadData}
        />
      )}
    </div>
  );
};

export default VehicleServiceTab;
